import React, { memo, forwardRef, useCallback, useMemo } from 'react';
import { useOverlay, useModal, OverlayContainer, OverlayProvider } from '@react-aria/overlays';
import { useDialog } from '@react-aria/dialog';
import cn from 'classnames';
import { ThemeProvider, useTheme } from '../ThemeProvider';
import { useForwardedRef } from '../../hooks';
import { ModalContext } from './contexts';
import styles from './styles.module.css';
export function useModalOverlay(props, ref) {
    const { dismissable, opened, onClose, size = 'm' } = props;
    const elRef = React.useRef(null);
    const { modalProps } = useModal();
    const { dialogProps, titleProps } = useDialog({}, elRef);
    const shouldCloseOnInteractOutside = useCallback((el) => {
        if (!(ref === null || ref === void 0 ? void 0 : ref.current)) {
            return true;
        }
        return ref.current.contains(el);
    }, []);
    const { overlayProps, underlayProps } = useOverlay({
        onClose,
        shouldCloseOnInteractOutside,
        isDismissable: dismissable !== false,
        isOpen: opened,
    }, elRef);
    return {
        underlayProps,
        titleProps,
        modalProps: {
            size,
            onClose,
            ...overlayProps,
            ...modalProps,
            ...dialogProps,
            ref: elRef,
        },
    };
}
export const Overlay = memo(forwardRef(function Overlay(props, ref) {
    const { children, dismissable, opened, zIndex, onClose, portal, style, className, ...rest } = props;
    const overlayRef = useForwardedRef(ref);
    const context = useModalOverlay(props, overlayRef);
    const theme = useTheme();
    const finalStyle = useMemo(() => (zIndex == null ? style : { zIndex, ...style }), [zIndex, style]);
    if (portal === false) {
        return (React.createElement(OverlayProvider, null,
            React.createElement(ModalContext.Provider, { value: context },
                React.createElement("div", { ...rest, className: cn(className, styles.overlay), style: finalStyle, ref: overlayRef }, children))));
    }
    return (React.createElement(OverlayContainer, null,
        React.createElement(ModalContext.Provider, { value: context },
            React.createElement(ThemeProvider, { theme: theme, ...rest, className: cn(className, styles.overlay), style: finalStyle, ref: overlayRef }, children))));
}));
